import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTheme } from '@mui/material/styles';

const pillButtonSx = (isDarkMode) => ({
  minWidth: 0,
  px: 1.25,
  py: 0.5,
  height: 30,
  borderRadius: 999,
  textTransform: 'none',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.01em',
  lineHeight: 1,
  border: '1px solid',
  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
  backgroundColor: isDarkMode ? '#0f172a' : '#ffffff',
  color: 'text.secondary',
  boxShadow: isDarkMode
    ? '0 1px 2px rgba(0,0,0,0.35)'
    : '0 1px 2px rgba(0,0,0,0.06)',
  '& .MuiButton-startIcon': {
    marginRight: 0.5,
    marginLeft: 0,
  },
  '&:hover': {
    backgroundColor: isDarkMode ? '#475569' : '#e5e7eb',
    borderColor: isDarkMode ? '#64748b' : '#d1d5db',
    color: 'text.primary',
  },
});

/**
 * Labeled action pills for Requests grids.
 * Open / Jobs / Unlock / Dismiss used for job-related rows.
 * Accept / Reject used for client request rows.
 */
const RequestsActionButtons = ({
  onOpenHere,
  onOpenInJobs,
  onUnlock,
  onDismiss,
  onAccept,
  onReject,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const buttonSx = pillButtonSx(isDarkMode);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        height: '100%',
        py: 0.5,
      }}
    >
      {onOpenHere && (
        <Tooltip title="Preview this request without leaving the page">
          <Button
            size="small"
            variant="outlined"
            startIcon={<VisibilityOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onOpenHere?.(e);
            }}
            aria-label="Open here"
            sx={buttonSx}
          >
            Open
          </Button>
        </Tooltip>
      )}
      {onOpenInJobs && (
        <Tooltip title="Open this job on the Jobs page">
          <Button
            size="small"
            variant="outlined"
            startIcon={<WorkOutlineIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onOpenInJobs?.(e);
            }}
            aria-label="Open in Jobs"
            sx={buttonSx}
          >
            Jobs
          </Button>
        </Tooltip>
      )}
      {onAccept && (
        <Tooltip title="Review and accept this client request">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onAccept?.(e);
            }}
            aria-label="Accept client request"
            sx={{
              ...buttonSx,
              color: isDarkMode ? '#86efac' : '#15803d',
              borderColor: isDarkMode ? '#166534' : '#86efac',
              '&:hover': {
                ...buttonSx['&:hover'],
                color: isDarkMode ? '#bbf7d0' : '#166534',
                borderColor: isDarkMode ? '#22c55e' : '#22c55e',
              },
            }}
          >
            Accept
          </Button>
        </Tooltip>
      )}
      {onUnlock && (
        <Tooltip title="Unlock this job and clear the unlock request">
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockOpenOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onUnlock?.(e);
            }}
            aria-label="Unlock job"
            sx={{
              ...buttonSx,
              color: isDarkMode ? '#86efac' : '#15803d',
              borderColor: isDarkMode ? '#166534' : '#86efac',
              '&:hover': {
                ...buttonSx['&:hover'],
                color: isDarkMode ? '#bbf7d0' : '#166534',
                borderColor: isDarkMode ? '#22c55e' : '#22c55e',
              },
            }}
          >
            Unlock
          </Button>
        </Tooltip>
      )}
      {onReject && (
        <Tooltip title="Reject and remove this client request">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CancelOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onReject?.(e);
            }}
            aria-label="Reject client request"
            sx={{
              ...buttonSx,
              color: isDarkMode ? '#fca5a5' : '#b91c1c',
              borderColor: isDarkMode ? '#7f1d1d' : '#fecaca',
              '&:hover': {
                ...buttonSx['&:hover'],
                color: isDarkMode ? '#fecaca' : '#991b1b',
                borderColor: isDarkMode ? '#ef4444' : '#f87171',
              },
            }}
          >
            Reject
          </Button>
        </Tooltip>
      )}
      {onDismiss && (
        <Tooltip title="Dismiss this unlock request without unlocking the job">
          <Button
            size="small"
            variant="outlined"
            startIcon={<CancelOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              e.stopPropagation();
              onDismiss?.(e);
            }}
            aria-label="Dismiss unlock request"
            sx={{
              ...buttonSx,
              color: isDarkMode ? '#fbbf24' : '#b45309',
              borderColor: isDarkMode ? '#92400e' : '#fcd34d',
              '&:hover': {
                ...buttonSx['&:hover'],
                color: isDarkMode ? '#fcd34d' : '#92400e',
                borderColor: isDarkMode ? '#b45309' : '#f59e0b',
              },
            }}
          >
            Dismiss
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

export default RequestsActionButtons;
